/**
 * useToastStore — global toast notification queue.
 *
 * WHY a Zustand store instead of a React context:
 * Toasts need to be triggered from non-React contexts (EventBus listeners wired inside
 * Phaser's update loop, network callbacks, etc.). Zustand's getState() / setState() are
 * callable outside the React tree without a Provider, which makes it the correct tool
 * for this cross-layer communication. A React context would require prop-drilling or a
 * ref hack to reach those layers.
 *
 * Each toast has a unique numeric `id` (auto-incremented). ToastNotification.tsx reads
 * this store and renders the queue. Callers use `addToast()` from anywhere.
 */

import { create } from 'zustand'

export type ToastVariant = 'info' | 'success' | 'warning' | 'error' | 'achievement'

export interface Toast {
  id: number
  message: string
  variant: ToastVariant
  /** Duration in ms before auto-dismiss. Default 3500. */
  durationMs: number
  /** Optional icon emoji/character shown left of message */
  icon?: string
}

interface ToastState {
  toasts: Toast[]
  addToast: (opts: { message: string; variant?: ToastVariant; durationMs?: number; icon?: string }) => void
  removeToast: (id: number) => void
}

let nextId = 1

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],

  addToast: ({ message, variant = 'info', durationMs = 3500, icon }) => {
    const id = nextId++
    set((s) => ({ toasts: [...s.toasts, { id, message, variant, durationMs, icon }] }))
    // Auto-dismiss after `durationMs` — store owns the timer so callers need not track it
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
    }, durationMs)
  },

  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))
